import { create } from 'zustand';
import request from '../utils/request';

const HEALTH_CHECK_INTERVAL = 30000;
const HEALTH_CHECK_TIMEOUT = 5000;

type ConnectionStatus = 'connected' | 'disconnected' | 'checking';

interface StatusState {
  status: ConnectionStatus;
  lastChecked: number | null;
  dbStatus: string;
  intervalId: ReturnType<typeof setInterval> | null;

  checkHealth: () => Promise<void>;
  startMonitoring: () => void;
  stopMonitoring: () => void;
}

export const useStatusStore = create<StatusState>()((set, get) => ({
  status: 'checking',
  lastChecked: null,
  dbStatus: 'unknown',
  intervalId: null,

  checkHealth: async () => {
    set({ status: 'checking' });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

    const response = await request
      .get('/grnt/health', { signal: controller.signal })
      .catch(() => null)
      .finally(() => clearTimeout(timeoutId));

    if (response?.data) {
      set({
        status: response.data.status === 'healthy' ? 'connected' : 'disconnected',
        dbStatus: response.data.database || 'unknown',
        lastChecked: Date.now(),
      });
    } else {
      set({
        status: 'disconnected',
        dbStatus: 'unknown',
        lastChecked: Date.now(),
      });
    }
  },

  startMonitoring: () => {
    const { intervalId, checkHealth } = get();
    if (intervalId) return;

    checkHealth();
    const id = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);
    set({ intervalId: id });
  },

  stopMonitoring: () => {
    const { intervalId } = get();
    if (intervalId) {
      clearInterval(intervalId);
      set({ intervalId: null });
    }
  },
}));
